import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';

/**
 * "Your public link" — a compact refer-&-earn-style card showing a
 * distributor/reseller their shareable profile URL (smartfi.ph/p/<slug>)
 * with Copy / Preview / Share-to-Facebook actions. They post this on
 * Facebook to sell credits; a shopper clicks it, sees their profile, taps
 * Buy. Used on both the Distributor and Reseller profile pages.
 */

// Which landing site hosts the /p/<slug> page. Explicit VITE_LANDING_URL wins;
// otherwise infer from the app host so staging links point at the staging
// landing instead of prod (which 404s — the /p page isn't on prod yet).
function landingBase(): string {
   const env = import.meta.env.VITE_LANDING_URL as string | undefined;
   if (env) return env;
   if (typeof window !== 'undefined') {
      const host = window.location.hostname;
      if (host.includes('app-staging'))
         return 'https://landing-staging.smartfi.ph';
   }
   return 'https://smartfi.ph';
}

export default function PartnerShareLink({ slug }: { slug?: string }) {
   const [copied, setCopied] = useState(false);
   if (!slug) return null;
   const url = `${landingBase()}/p/${slug}`;

   const copy = async () => {
      try {
         await navigator.clipboard.writeText(url);
         setCopied(true);
         toast.success('Link copied — paste it on Facebook to sell credits');
         setTimeout(() => setCopied(false), 2000);
      } catch {
         toast.error('Could not copy — long-press the link to copy it');
      }
   };

   const fbShare = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      url
   )}`;

   return (
      <Card>
         <Glow />
         <Head>
            <IconBadge>🔗</IconBadge>
            <div>
               <Title>Your public link</Title>
               <Sub>Share on Facebook — customers buy credits straight from you.</Sub>
            </div>
         </Head>

         <UrlPill onClick={copy} title="Click to copy">
            <UrlText>{url}</UrlText>
            <CopyChip $copied={copied}>{copied ? '✓ Copied' : 'Copy'}</CopyChip>
         </UrlPill>

         <Actions>
            <GhostBtn href={url} target="_blank" rel="noopener noreferrer">
               Preview
            </GhostBtn>
            <FbBtn href={fbShare} target="_blank" rel="noopener noreferrer">
               <FbGlyph>f</FbGlyph> Share to Facebook
            </FbBtn>
         </Actions>
      </Card>
   );
}

const float = keyframes`
   0%,100% { transform: translate(0,0); }
   50% { transform: translate(10px,-12px); }
`;

const Card = styled.section`
   position: relative;
   overflow: hidden;
   border-radius: 18px;
   padding: 18px;
   color: #fff;
   background: linear-gradient(135deg, #3760f9 0%, #6a4dff 55%, #8b5cf6 100%);
   box-shadow: 0 12px 30px rgba(55, 96, 249, 0.28);
`;

const Glow = styled.div`
   position: absolute;
   top: -40px;
   right: -30px;
   width: 160px;
   height: 160px;
   border-radius: 50%;
   background: radial-gradient(circle, rgba(255, 255, 255, 0.22), transparent 70%);
   animation: ${float} 6s ease-in-out infinite;
   pointer-events: none;
`;

const Head = styled.div`
   position: relative;
   display: flex;
   gap: 11px;
   align-items: flex-start;
   margin-bottom: 14px;
`;

const IconBadge = styled.div`
   flex-shrink: 0;
   width: 38px;
   height: 38px;
   border-radius: 11px;
   display: grid;
   place-items: center;
   font-size: 18px;
   background: rgba(255, 255, 255, 0.18);
`;

const Title = styled.p`
   font-size: 15px;
   font-weight: 800;
   margin: 0;
   line-height: 1.2;
`;

const Sub = styled.p`
   font-size: 12px;
   margin: 3px 0 0;
   color: rgba(255, 255, 255, 0.85);
   line-height: 1.35;
`;

const UrlPill = styled.button`
   position: relative;
   width: 100%;
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 8px 8px 8px 14px;
   border: none;
   border-radius: 12px;
   background: rgba(255, 255, 255, 0.15);
   cursor: pointer;
   text-align: left;
   transition: background 0.15s ease;
   &:hover {
      background: rgba(255, 255, 255, 0.22);
   }
`;

const UrlText = styled.span`
   flex: 1;
   min-width: 0;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
   font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
   font-size: 13px;
   color: #fff;
`;

const CopyChip = styled.span<{ $copied?: boolean }>`
   flex-shrink: 0;
   padding: 7px 14px;
   border-radius: 9px;
   font-size: 12px;
   font-weight: 800;
   color: ${({ $copied }) => ($copied ? '#fff' : '#3760f9')};
   background: ${({ $copied }) => ($copied ? 'rgba(22,163,74,0.95)' : '#fff')};
   transition: all 0.15s ease;
`;

const Actions = styled.div`
   position: relative;
   display: flex;
   gap: 9px;
   margin-top: 12px;
`;

const GhostBtn = styled.a`
   flex: 1;
   text-align: center;
   padding: 9px 12px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 700;
   text-decoration: none;
   color: #fff;
   background: rgba(255, 255, 255, 0.14);
   border: 1px solid rgba(255, 255, 255, 0.3);
   &:hover {
      background: rgba(255, 255, 255, 0.24);
   }
`;

const FbBtn = styled.a`
   flex: 1.4;
   display: flex;
   align-items: center;
   justify-content: center;
   gap: 8px;
   padding: 9px 12px;
   border-radius: 10px;
   font-size: 13px;
   font-weight: 800;
   text-decoration: none;
   color: #1877f2;
   background: #fff;
   &:hover {
      opacity: 0.92;
   }
`;

const FbGlyph = styled.span`
   display: grid;
   place-items: center;
   width: 18px;
   height: 18px;
   border-radius: 5px;
   background: #1877f2;
   color: #fff;
   font-weight: 900;
   font-size: 12px;
   font-family: Georgia, serif;
`;
